import React, { createContext, useCallback, useContext } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { usePrinter } from '../hooks/usePrinter';
import ThermalReceipt80mm from '../components/pos/receipt/ThermalReceipt80mm';
import ThermalReturnReceipt80mm from '../components/pos/receipt/ThermalReturnReceipt80mm';
import ThermalExchangeReceipt80mm from '../components/pos/receipt/ThermalExchangeReceipt80mm';
import { printHtml } from '../services/print/browserPrintProvider';
import {
  buildReceiptLines,
  buildReturnReceiptLines,
  buildExchangeReceiptLines,
} from '../components/pos/receipt/buildEscposLines';

const PAGE_CSS = `
  html, body { margin: 0; padding: 0; background: #fff; }
  @page { margin: 0; } /* page size comes from the thermal printer driver */
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;

const toFullHtml = (bodyHtml) =>
  `<!doctype html><html><head><meta charset="utf-8"/><style>${PAGE_CSS}</style></head><body>${bodyHtml}</body></html>`;

// ── Iframe-based browser print (no navigation, stays on current page) ──────────

export function browserPrintOrder(receipt) {
  const html = renderToStaticMarkup(<ThermalReceipt80mm receipt={receipt} />);
  printHtml({ title: `Receipt ${receipt.billNumber || ''}`, html, styles: PAGE_CSS });
}

export function browserPrintReturn(receipt) {
  const html = renderToStaticMarkup(<ThermalReturnReceipt80mm receipt={receipt} />);
  printHtml({ title: `Return ${receipt.returnNumber || ''}`, html, styles: PAGE_CSS });
}

export function browserPrintExchange(receipt) {
  const html = renderToStaticMarkup(<ThermalExchangeReceipt80mm receipt={receipt} />);
  printHtml({ title: `Exchange ${receipt.exchangeNumber || ''}`, html, styles: PAGE_CSS });
}

// ── Context ────────────────────────────────────────────────────────────────────

const PrinterContext = createContext(null);

export function PrinterProvider({ children }) {
  const { isConnected, status, printReceipt } = usePrinter({
    autoConnect: true,
    onConnect: () => console.log('[Printer] Connected to companion app'),
    onDisconnect: () => console.log('[Printer] Disconnected from companion app'),
  });

  const silentPrintOrder = useCallback(async (receipt) => {
    return printReceipt({ lines: buildReceiptLines(receipt) });
  }, [printReceipt]);

  const silentPrintReturn = useCallback(async (receipt) => {
    return printReceipt({ lines: buildReturnReceiptLines(receipt) });
  }, [printReceipt]);

  const silentPrintExchange = useCallback(async (receipt) => {
    return printReceipt({ lines: buildExchangeReceiptLines(receipt) });
  }, [printReceipt]);

  return (
    <PrinterContext.Provider value={{ isConnected, status, silentPrintOrder, silentPrintReturn, silentPrintExchange }}>
      {children}
    </PrinterContext.Provider>
  );
}

const DISCONNECTED_DEFAULT = {
  isConnected: false,
  status: 'disconnected',
  silentPrintOrder: () => Promise.reject(new Error('Printer not available')),
  silentPrintReturn: () => Promise.reject(new Error('Printer not available')),
  silentPrintExchange: () => Promise.reject(new Error('Printer not available')),
};

export function usePrinterContext() {
  return useContext(PrinterContext) ?? DISCONNECTED_DEFAULT;
}
